import React, { useState, useEffect, useCallback } from "react";
import { get } from "../../utils/request";
import { post } from "../../utils/request";
import { t, noAutoTranslateUserTextProps } from "../../utils/i18n";
import styles from "./VoteSection.module.css";

/**
 * 公共投票组件：由各模块详情接口返回的 voteId 控制显隐，voteId 为空不展示，不为空则 GET /api/social/veto/{id} 拉取投票信息
 * - 未登录可查看投票结果；仅在做“投票”操作时要求登录
 */
const VoteSection = ({ voteId, isLoggedIn, onRequireLogin }) => {
  const [voteDetail, setVoteDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRequireLoginHint, setShowRequireLoginHint] = useState(false);
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const effectiveVoteId = voteId != null && voteId !== "" ? String(voteId).trim() : "";

  const fetchVoteDetail = useCallback(async () => {
    if (!effectiveVoteId) return;
    try {
      const res = await get(`/social/veto/${effectiveVoteId}`);
      if (res?.success && res?.data) {
        setVoteDetail(res.data);
      } else {
        setVoteDetail(null);
      }
    } catch (e) {
      setVoteDetail(null);
    }
  }, [effectiveVoteId]);

  useEffect(() => {
    if (!effectiveVoteId) return;
    setVoteDetail(null);
    fetchVoteDetail();
  }, [effectiveVoteId, fetchVoteDetail]);

  if (!effectiveVoteId) return null;

  const normalizedChoiceType = String(voteDetail?.choiceType || "")
    .trim()
    .toUpperCase();
  const isMulti =
    normalizedChoiceType === "CHECK_BOX" || normalizedChoiceType === "MULTI";
  const maxSelected = isMulti ? voteDetail?.maxSelectedCnt ?? 1 : 1;

  const openModal = () => {
    if (voteDetail?.voted) return;
    if (isLoggedIn !== true) {
      setShowRequireLoginHint(true);
      return;
    }
    const defaultSelectedOptionIds =
      voteDetail?.options?.filter(o => o.selected).map(o => o.id) ?? [];
    setSelectedOptionIds(
      isMulti ? defaultSelectedOptionIds : defaultSelectedOptionIds.slice(0, 1)
    );
    setSubmitError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSubmitError(null);
  };

  const toggleOption = (optionId) => {
    setSelectedOptionIds(prev => {
      if (isMulti) {
        if (prev.includes(optionId)) return prev.filter(id => id !== optionId);
        if (prev.length >= maxSelected) return prev;
        return [...prev, optionId];
      }
      return [optionId];
    });
  };

  const handleSubmitVote = async () => {
    if (selectedOptionIds.length === 0) {
      setSubmitError(t("vote.error.selectOne", "Please select at least one option"));
      return;
    }
    if (!isMulti && selectedOptionIds.length > 1) {
      setSubmitError(
        t("vote.error.singleOnly", "Single-choice voting allows only one option")
      );
      return;
    }
    if (isLoggedIn !== true) {
      setShowRequireLoginHint(true);
      return;
    }
    if (voteDetail?.headerId == null) {
      setSubmitError(
        t(
          "vote.error.invalidData",
          "Vote data is invalid, please refresh and try again"
        )
      );
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await post("/social/veto/do", {
        headerId: voteDetail.headerId,
        optionIdList: selectedOptionIds,
      });
      if (res?.success === true || res?.code === 0) {
        await fetchVoteDetail();
        closeModal();
      } else {
        setSubmitError(
          res?.message || t("vote.error.submit", "Submission failed, please try again")
        );
      }
    } catch (e) {
      setSubmitError(
        e?.message || t("vote.error.submit", "Submission failed, please try again")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const hasVoteData = voteDetail?.options?.length > 0;

  if (!hasVoteData) {
    return (
      <>
        <div
          className={`${styles.voteSection} ${styles.voteSectionClickable} ${styles.voteSectionPlaceholder}`}
          onClick={() => (isLoggedIn !== true ? setShowRequireLoginHint(true) : null)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (isLoggedIn !== true) setShowRequireLoginHint(true);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={t("vote.aria.participate", "participate in voting")}
        >
          <div className={styles.voteTitle}>{t("vote.title.default", "vote")}</div>
          <div className={styles.voteMeta}>
            <span className={styles.voteClickHint}>
              {t(
                "vote.meta.placeholderHint",
                "Participate in voting after logging in · Click to participate"
              )}
            </span>
          </div>
        </div>
        {showRequireLoginHint && (
          <div
            className={styles.requireLoginOverlay}
            onClick={() => setShowRequireLoginHint(false)}
          >
            <div
              className={styles.requireLoginContent}
              onClick={e => e.stopPropagation()}
            >
              <button
                type="button"
                className={styles.requireLoginClose}
                onClick={() => setShowRequireLoginHint(false)}
                aria-label={t("aria.close", "close")}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
              <p className={styles.requireLoginText}>
                {t("vote.login.prefix", "Please ")}
                <span
                  className={styles.requireLoginLink}
                  onClick={() => {
                    setShowRequireLoginHint(false);
                    setTimeout(() => onRequireLogin?.(), 0);
                  }}
                >
                  {t("vote.login.link", "Log in")}
                </span>
                {t("vote.login.suffix", " first")}
              </p>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={`${styles.voteSection} ${!voteDetail.voted ? styles.voteSectionClickable : ""}`}
        onClick={!voteDetail.voted ? openModal : undefined}
        onKeyDown={e => {
          if (!voteDetail.voted && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            openModal();
          }
        }}
        role={!voteDetail.voted ? "button" : undefined}
        tabIndex={!voteDetail.voted ? 0 : undefined}
        aria-label={!voteDetail.voted ? "Click to participate in voting" : undefined}
      >
        <div className={styles.voteTitle}>
          {voteDetail.title ? (
            <span
              className="notranslate"
              {...noAutoTranslateUserTextProps}
            >
              {voteDetail.title}
            </span>
          ) : (
            t("vote.title.default", "vote")
          )}
        </div>
        <div className={styles.voteMeta}>
          {t("vote.meta.participants", "{count} people participated", {
            count: voteDetail.participantsCnt ?? 0,
          })}
          {isMulti && (
            <span className={styles.voteMultiHint}>
              {t("vote.meta.maxSelect", "(Max select {max} items)", {
                max: maxSelected,
              })}
            </span>
          )}
          {!voteDetail.voted && (
            <span className={styles.voteClickHint}>
              {isLoggedIn !== true
                ? t(
                    "vote.meta.loginHint",
                    " · Participate in voting after logging in"
                  )
                : t("vote.meta.clickHint", " · Click to participate")}
            </span>
          )}
        </div>
        <div className={styles.voteOptions}>
          {voteDetail.options.map(opt => (
            <div
              key={opt.id}
              className={`${styles.voteOption} ${opt.selected ? styles.voteSelected : ""}`}
            >
              {opt.icon && (
                <img
                  src={opt.icon}
                  alt=""
                  className={styles.voteOptionIcon}
                />
              )}
              <span
                className={[styles.voteOptionName, "notranslate"]
                  .filter(Boolean)
                  .join(" ")}
                {...noAutoTranslateUserTextProps}
              >
                {opt.name}
              </span>
              <div className={styles.voteBar}>
                <div
                  className={styles.voteBarFill}
                  style={{
                    width: `${((opt.ratio ?? 0) * 100).toFixed(1)}%`,
                  }}
                />
              </div>
              <span className={styles.voteRatio}>
                {((opt.ratio ?? 0) * 100).toFixed(1)}%
              </span>
              <span className={styles.voteCnt}>{opt.selectedCnt ?? 0} votes</span>
            </div>
          ))}
        </div>
        {voteDetail.voted && (
          <p className={styles.votedHint}>
            {t("vote.voted.hint", "You have participated in the vote")}
          </p>
        )}
      </div>

      {/* 未登录提示：Please Log in first（与评论区一致） */}
      {showRequireLoginHint && (
        <div
          className={styles.requireLoginOverlay}
          onClick={() => setShowRequireLoginHint(false)}
        >
          <div
            className={styles.requireLoginContent}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.requireLoginClose}
              onClick={() => setShowRequireLoginHint(false)}
              aria-label={t("aria.close", "close")}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <p className={styles.requireLoginText}>
              {t("vote.login.prefix", "Please ")}
              <span
                className={styles.requireLoginLink}
                onClick={() => {
                  setShowRequireLoginHint(false);
                  setTimeout(() => onRequireLogin?.(), 0);
                }}
              >
                {t("vote.login.link", "Log in")}
              </span>
              {t("vote.login.suffix", " first")}
            </p>
          </div>
        </div>
      )}

      {showModal && (
        <div className={styles.voteModalOverlay} onClick={closeModal}>
          <div
            className={styles.voteModal}
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.voteModalHeader}>
              <h3 className={styles.voteModalTitle}>
                {voteDetail.title ? (
                  <span
                    className="notranslate"
                    {...noAutoTranslateUserTextProps}
                  >
                    {voteDetail.title}
                  </span>
                ) : (
                  t("vote.title.default", "vote")
                )}
              </h3>
              <button
                type="button"
                className={styles.voteModalClose}
                onClick={closeModal}
                aria-label={t("aria.close", "close")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className={styles.voteModalBody}>
              <p className={styles.voteModalMeta}>
                {t("vote.meta.participants", "{count} people participated", {
                  count: voteDetail.participantsCnt ?? 0,
                })}
                {isMulti &&
                  ` · ${t("vote.meta.maxSelect", "Max select {max} items", {
                    max: maxSelected,
                  })}`}
              </p>
              <div className={styles.voteModalOptions}>
                {voteDetail.options.map(opt => (
                  <label
                    key={opt.id}
                    className={`${styles.voteModalOption} ${selectedOptionIds.includes(opt.id) ? styles.voteModalOptionSelected : ""} ${isMulti && !selectedOptionIds.includes(opt.id) && selectedOptionIds.length >= maxSelected ? styles.voteModalOptionDisabled : ""}`}
                  >
                    <input
                      type={isMulti ? "checkbox" : "radio"}
                      name="voteOption"
                      checked={selectedOptionIds.includes(opt.id)}
                      onChange={() => toggleOption(opt.id)}
                      disabled={isMulti && !selectedOptionIds.includes(opt.id) && selectedOptionIds.length >= maxSelected}
                    />
                    {opt.icon && (
                      <img
                        src={opt.icon}
                        alt=""
                        className={styles.voteOptionIcon}
                      />
                    )}
                    <span
                      className="notranslate"
                      {...noAutoTranslateUserTextProps}
                    >
                      {opt.name}
                    </span>
                  </label>
                ))}
              </div>
              {submitError && (
                <p className={styles.voteSubmitError}>{submitError}</p>
              )}
            </div>
            <div className={styles.voteModalActions}>
              <button
                type="button"
                className={styles.voteModalBtnSecondary}
                onClick={closeModal}
              >
                {t("vote.modal.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className={styles.voteModalBtnPrimary}
                onClick={handleSubmitVote}
                disabled={submitting}
              >
                {submitting
                  ? t("vote.modal.submitting", "Submitting...")
                  : t("vote.modal.submit", "Submit vote")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VoteSection;
